<!-- ─── Header ─── -->
<div class="dialog-header">
  <div class="dialog-header__text">
    <h2>{{ isEdit ? 'Edit contact' : 'New contact' }}</h2>
    <p>{{ isEdit ? 'Update the contact details below' : 'Fill in the details to add a new contact' }}</p>
  </div>
  <button class="dialog-header__close" (click)="cancel()" aria-label="Close dialog">
    <mat-icon>close</mat-icon>
  </button>
</div>

<app-autosave-status [autosave]="autosave" [compact]="true" style="padding: 8px 24px 0"></app-autosave-status>

<!-- ─── Scan business card (top, mobile camera) ─── -->
<div class="scan-bar" *ngIf="!isEdit">
  <label class="scan-btn" [class.scan-btn--busy]="scanning">
    <input
      type="file"
      accept="image/*"
      capture="environment"
      hidden
      [disabled]="scanning"
      (change)="onCardSelected($event)"
    />
    <mat-icon>{{ scanning ? 'hourglass_top' : 'photo_camera' }}</mat-icon>
    <span>{{ scanning ? 'Reading card…' : 'Scan business card' }}</span>
  </label>
  <span class="scan-bar__hint">Take a photo and we’ll fill the form for you</span>
</div>

<div
  class="scan-result"
  *ngIf="scanStatus !== 'idle'"
  [class.scan-result--ok]="scanStatus === 'ok'"
  [class.scan-result--partial]="scanStatus === 'partial'"
  [class.scan-result--error]="scanStatus === 'error'"
>
  <mat-icon>{{ scanStatus === 'ok' ? 'check_circle' : scanStatus === 'error' ? 'error' : 'info' }}</mat-icon>
  <div class="scan-result__body">
    <span class="scan-result__msg">{{ scanMessage }}</span>
    <span class="scan-result__hint" *ngIf="scanWarnings.length">{{ scanWarnings.join(' · ') }}</span>
  </div>
</div>

<!-- ─── Duplicate warning ─── -->
<div class="dup-banner" *ngIf="duplicates.length && !duplicateOverridden">
  <div class="dup-banner__head">
    <mat-icon>warning</mat-icon>
    <strong>Possible duplicate{{ duplicates.length > 1 ? 's' : '' }}</strong>
    <button class="dup-banner__dismiss" type="button" (click)="dismissDuplicates()">
      Continue anyway
    </button>
  </div>
  <ul class="dup-banner__list">
    <li *ngFor="let d of duplicates" class="dup-banner__item">
      <button
        type="button"
        class="dup-banner__row"
        (click)="openMatch(d)"
        [title]="'Open and edit ' + (d.firstName || '') + ' ' + (d.lastName || '')"
      >
        <span class="dup-banner__text">
          <span class="dup-banner__name">{{ d.firstName }} {{ d.lastName }}</span>
          <span class="dup-banner__meta">
            {{ [d.company, d.email, d.mobilePhone] | bcJoinTruthy:' · ' }}
          </span>
        </span>
        <span class="dup-banner__open">
          <mat-icon>edit</mat-icon>
          <span>Open</span>
        </span>
      </button>
    </li>
  </ul>
</div>

<!-- ─── Section Tabs ─── -->
<div class="section-tabs" role="group" aria-label="Form sections">
  <button
    *ngFor="let section of sections"
    type="button"
    class="section-tab"
    [class.section-tab--active]="activeSection === section.key"
    [attr.aria-pressed]="activeSection === section.key"
    [class.section-tab--error]="hasSectionError(section.key)"
    (click)="setSection(section.key)"
  >
    <mat-icon class="section-tab__icon" aria-hidden="true">{{ section.icon }}</mat-icon>
    <span>{{ section.label }}</span>
    <span *ngIf="hasSectionError(section.key)" class="section-tab__error-dot" aria-hidden="true"></span>
    <span *ngIf="hasSectionError(section.key)" class="bc-sr-only">(has errors)</span>
  </button>
</div>

<!-- ─── Form Body ─── -->
<div class="dialog-body" [formGroup]="form">

  <!-- Personal Section -->
  <div class="form-section" *ngIf="activeSection === 'personal'">
    <div class="form-row">
      <div class="form-field" [class.form-field--error]="isFieldInvalid('firstName')">
        <label class="form-label">
          First name <span class="required">*</span>
        </label>
        <input
          class="form-input"
          formControlName="firstName"
          placeholder="e.g. Elena"
          cdkFocusInitial
        />
        <span class="form-error" *ngIf="hasError('firstName', 'required')">
          <mat-icon>info</mat-icon> First name is required
        </span>
        <span class="form-error" *ngIf="hasError('firstName', 'minlength')">
          <mat-icon>info</mat-icon> At least 2 characters
        </span>
      </div>

      <div class="form-field">
        <label class="form-label">Last name</label>
        <input
          class="form-input"
          formControlName="lastName"
          placeholder="e.g. Christodoulou"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-field" [class.form-field--error]="isFieldInvalid('email')">
        <label class="form-label">Email</label>
        <input
          class="form-input"
          type="email"
          inputmode="email"
          formControlName="email"
          placeholder="e.g. elena@company.com"
        />
        <span class="form-error" *ngIf="hasError('email', 'email')">
          <mat-icon>info</mat-icon> Invalid email format
        </span>
      </div>

      <div class="form-field">
        <label class="form-label">Mobile phone</label>
        <input
          class="form-input"
          type="tel"
          inputmode="tel"
          formControlName="mobilePhone"
          placeholder="e.g. +357 96 123456"
        />
      </div>
    </div>

    <div class="form-row form-row--single">
      <div class="form-field">
        <label class="form-label">Work phone</label>
        <input
          class="form-input"
          type="tel"
          inputmode="tel"
          formControlName="workPhone"
          placeholder="e.g. +357 25 123456"
        />
      </div>
    </div>

    <!-- Preferred ways of contact -->
    <div class="form-field">
      <label class="form-label">
        Preferred ways of contact
        <span class="form-hint">How this person prefers to be reached</span>
      </label>
      <div class="channel-chips">
        <button
          *ngFor="let opt of contactMethodOptions"
          type="button"
          class="channel-chip"
          [class.channel-chip--active]="isMethodSelected(opt.value)"
          (click)="toggleContactMethod(opt.value)"
        >
          <mat-icon>{{ opt.icon }}</mat-icon>
          <span>{{ opt.label }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Work Section -->
  <div class="form-section" *ngIf="activeSection === 'work'">
    <div class="form-row">
      <div class="form-field" [class.form-field--error]="isFieldInvalid('contactType')">
        <label class="form-label">
          Contact type <span class="required">*</span>
        </label>
        <bc-searchable-select
          formControlName="contactType"
          placeholder="Select contact type"
          [options]="contactTypeSelectOptions"
        ></bc-searchable-select>
        <span class="form-error" *ngIf="hasError('contactType', 'required')">
          <mat-icon>info</mat-icon> Please select a type
        </span>
      </div>

      <div class="form-field">
        <label class="form-label">Company</label>
        <input
          class="form-input"
          formControlName="company"
          placeholder="e.g. Bizcombo Tech"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">Job title</label>
        <input
          class="form-input"
          formControlName="jobTitle"
          placeholder="e.g. Senior Auditor"
        />
      </div>

      <div class="form-field">
        <label class="form-label">Department</label>
        <input
          class="form-input"
          formControlName="department"
          placeholder="e.g. Finance"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">TIC</label>
        <input
          class="form-input"
          formControlName="tic"
          placeholder="e.g. 12345678X"
        />
      </div>

      <div class="form-field">
        <label class="form-label">VAT number</label>
        <input
          class="form-input"
          formControlName="vatNumber"
          placeholder="e.g. CY12345678X"
        />
      </div>
    </div>
  </div>

  <!-- Lead Section -->
  <div class="form-section" *ngIf="activeSection === 'lead'">
    <div class="form-row form-row--single">
      <div class="form-field">
        <label class="form-label">
          Lead rating
          <span class="lead-rating__hint">{{ form.value.leadRating }}/10 ·
            {{ form.value.leadRating <= 3 ? 'Cold' : form.value.leadRating >= 8 ? 'Hot' : 'Warm' }}
          </span>
        </label>
        <div class="lead-rating">
          <span class="lead-rating__label lead-rating__label--cold">Cold</span>
          <input
            class="lead-rating__slider"
            type="range"
            min="1"
            max="10"
            step="1"
            formControlName="leadRating"
          />
          <span class="lead-rating__label lead-rating__label--hot">Hot</span>
        </div>
      </div>
    </div>

    <div class="form-row form-row--single">
      <div class="form-field">
        <label class="form-label">Industry / category</label>
        <bc-searchable-select
          formControlName="industry"
          placeholder="Select or type an industry"
          emptyLabel="— No industry —"
          [allowCustom]="true"
          [loading]="industriesLoading"
          [options]="industrySelectOptions"
        ></bc-searchable-select>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">Person in charge</label>
        <bc-searchable-select
          formControlName="ownerAzureObjectId"
          placeholder="Choose owner"
          emptyLabel="— Unassigned —"
          [loading]="teamMembersLoading"
          [options]="teamMemberSelectOptions"
        ></bc-searchable-select>
      </div>

      <div class="form-field">
        <label class="form-label">Introducer</label>
        <bc-searchable-select
          formControlName="introducerId"
          placeholder="Choose introducer"
          emptyLabel="— None —"
          addLabel="Add new introducer"
          [loading]="introducersLoading"
          [options]="introducerSelectOptions"
          (addClicked)="startAddIntroducer()"
        ></bc-searchable-select>
      </div>
    </div>

    <!-- ─── Inline new-introducer mini-form ─── -->
    <div class="add-introducer" *ngIf="addingIntroducer">
      <div class="add-introducer__head">
        <mat-icon>person_add</mat-icon>
        <strong>New introducer</strong>
        <button type="button" class="add-introducer__close" (click)="cancelAddIntroducer()" aria-label="Cancel">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <p class="add-introducer__hint">
        Save just the basics now — the record is created as <em>Pending</em> so you can complete the rest on the introducers page.
      </p>

      <div class="add-introducer__row">
        <input
          class="form-input"
          [(ngModel)]="newIntroducer.name"
          [ngModelOptions]="{ standalone: true }"
          placeholder="Name *"
        />
      </div>
      <div class="add-introducer__row add-introducer__row--two">
        <input
          class="form-input"
          type="email"
          [(ngModel)]="newIntroducer.email"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onNewIntroducerContactChange()"
          placeholder="Email *"
        />
        <input
          class="form-input"
          type="tel"
          inputmode="tel"
          [(ngModel)]="newIntroducer.phone"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onNewIntroducerContactChange()"
          placeholder="Phone"
        />
      </div>

      <!-- Duplicate introducer warning -->
      <div class="dup-banner dup-banner--inline" *ngIf="introducerDuplicates.length">
        <div class="dup-banner__head">
          <mat-icon>warning</mat-icon>
          <strong>Possible existing introducer</strong>
        </div>
        <ul class="dup-banner__list">
          <li class="dup-banner__item" *ngFor="let d of introducerDuplicates">
            <button type="button" class="dup-banner__row" (click)="useExistingIntroducer(d)">
              <span class="dup-banner__text">
                <span class="dup-banner__name">{{ d.name }}</span>
                <span class="dup-banner__meta">
                  {{ [d.email, d.phone] | bcJoinTruthy:' · ' }}
                </span>
              </span>
              <span class="dup-banner__open">
                <mat-icon>arrow_forward</mat-icon>
                <span>Use this</span>
              </span>
            </button>
          </li>
        </ul>
      </div>

      <div class="add-introducer__actions">
        <button type="button" class="btn btn--secondary" (click)="cancelAddIntroducer()">Cancel</button>
        <button
          type="button"
          class="btn btn--primary"
          (click)="saveNewIntroducer()"
          [disabled]="!canSaveNewIntroducer() || savingIntroducer"
        >
          <mat-icon>{{ savingIntroducer ? 'hourglass_top' : 'check' }}</mat-icon>
          {{ savingIntroducer ? 'Saving…' : 'Save introducer' }}
        </button>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">LinkedIn</label>
        <input
          class="form-input"
          type="url"
          inputmode="url"
          formControlName="linkedInUrl"
          placeholder="linkedin.com/in/…"
        />
      </div>

      <div class="form-field">
        <label class="form-label">Website</label>
        <input
          class="form-input"
          type="url"
          inputmode="url"
          formControlName="website"
          placeholder="company.com"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">Facebook</label>
        <input
          class="form-input"
          formControlName="facebook"
          placeholder="facebook.com/…"
        />
      </div>

      <div class="form-field">
        <label class="form-label">Instagram</label>
        <input
          class="form-input"
          formControlName="instagram"
          placeholder="@handle"
        />
      </div>
    </div>
  </div>

  <!-- Location Section -->
  <div class="form-section" *ngIf="activeSection === 'location'">
    <div class="form-row form-row--single">
      <div class="form-field">
        <label class="form-label">Address</label>
        <input
          class="form-input"
          formControlName="address"
          placeholder="e.g. 25 Makarios Avenue"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-field">
        <label class="form-label">City</label>
        <input
          class="form-input"
          formControlName="city"
          placeholder="e.g. Limassol"
        />
      </div>

      <div class="form-field">
        <label class="form-label">Country</label>
        <input
          class="form-input"
          formControlName="country"
          placeholder="e.g. Cyprus"
        />
      </div>
    </div>
  </div>

  <!-- Notes Section -->
  <div class="form-section" *ngIf="activeSection === 'notes'">
    <div class="form-field">
      <label class="form-label">
        General note
        <span class="form-hint" *ngIf="isEdit">— overall description (editable). For dated entries use the thread below.</span>
      </label>
      <textarea
        class="form-textarea"
        formControlName="notes"
        rows="4"
        placeholder="Any general info about this contact…"
      ></textarea>
    </div>

    <!-- Timestamped notes thread — needs an existing contact id -->
    <section class="notes-thread" *ngIf="isEdit && data.contact">
      <div class="notes-thread__header">
        <mat-icon class="notes-thread__icon">forum</mat-icon>
        <span class="notes-thread__title">Activity notes</span>
        <span class="notes-thread__count" *ngIf="notes.length">{{ notes.length }}</span>
      </div>

      <div class="notes-composer">
        <textarea
          class="notes-composer__input"
          [(ngModel)]="newNoteBody"
          [ngModelOptions]="{ standalone: true }"
          placeholder="Add a new note — it will be saved with your name and the time."
          rows="3"
          [disabled]="addingNote"
        ></textarea>
        <div class="notes-composer__actions">
          <button
            class="notes-composer__send"
            type="button"
            (click)="addNote()"
            [disabled]="!newNoteBody.trim() || addingNote"
          >
            <mat-icon>{{ addingNote ? 'hourglass_top' : 'send' }}</mat-icon>
            <span>{{ addingNote ? 'Adding…' : 'Add note' }}</span>
          </button>
        </div>
      </div>

      <div class="notes-list" *ngIf="notes.length; else dialogNotesEmpty">
        <article class="note-entry" *ngFor="let n of notes">
          <div class="note-entry__avatar">
            {{ (n.authorName || '?').charAt(0).toUpperCase() }}
          </div>
          <div class="note-entry__body">
            <header class="note-entry__head">
              <span class="note-entry__author">{{ n.authorName || 'Unknown' }}</span>
              <span class="note-entry__time" [title]="n.createdAt">{{ formatNoteTime(n.createdAt) }}</span>
            </header>
            <p class="note-entry__text">{{ n.body }}</p>
            <button
              class="note-entry__delete"
              type="button"
              (click)="deleteNote(n)"
              aria-label="Delete note"
              title="Delete note"
            >
              <mat-icon>delete_outline</mat-icon>
            </button>
          </div>
        </article>
      </div>

      <ng-template #dialogNotesEmpty>
        <p class="notes-list__empty" *ngIf="!notesLoading">No activity notes yet — add the first one above.</p>
        <p class="notes-list__empty" *ngIf="notesLoading">Loading notes…</p>
      </ng-template>
    </section>
  </div>

</div>

<!-- ─── Footer ─── -->
<div class="dialog-footer">
  <span class="dialog-footer__hint">
    <span class="required">*</span> Required fields
  </span>

  <div class="dialog-footer__actions">
    <button class="btn btn--secondary" (click)="cancel()">Cancel</button>
    <button class="btn btn--primary" (click)="save()">
      <mat-icon>check</mat-icon>
      {{ isEdit ? 'Save changes' : 'Add contact' }}
    </button>
  </div>
</div>
